import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import 'react-data-grid/lib/styles.css';
import { useApolloClient } from '@apollo/client';
import productSalesMasterListQuery  from 'gql/dashboard/sales/customerHistory.gql';
import DefaultCurrency from 'components/DefaultCurrency';
import AdvancedTable from 'components/advanced-table/AdvancedTable';
import { toast } from 'react-toastify';
import { useAppContext } from 'contexts/AppContext';
import { useFormContext } from 'contexts/FormContext';
import { useFormattedDate } from 'hooks/useFormattedDate';
import TopToolBar from 'components/advanced-table/TopToolBar';
import LoadingOverlay from 'components/LoadingOverlay';

const CustomerOrderHistory = () => {
  const { appState } = useAppContext();
  const { formState, setFormState } = useFormContext();
  const apollo = useApolloClient();


  const [data, setData] = useState(undefined);
  const [tableData, setTableData] = useState(undefined);
  const [groupBy, setGroupBy] = useState([]);
  const [expandedGroupIds, setExpandedGroupIds] = useState(() => new Set());
  const [sortColumns, setSortColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterList, setFilterList] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: undefined,
    dateTo: undefined,
    itemGroup: [],
    quantityLowerBound: undefined,
    quantityUpperBound: undefined,
    priceLowerBound: undefined,
    priceUpperBound: undefined,
    incomeLowerBound: undefined,
    incomeUpperBound: undefined,
    search: undefined
  });

  const {mghDateFormater} = useFormattedDate();


  const dateFormater = value => {
    return mghDateFormater(value, 'date', false, false);
  };

  const isItemValidated = item => {
    // Search
    if(filters?.search){
      if( !item?.dsescription?.toLowerCase().includes(filters?.search?.toLowerCase()) &&
          !item?.itemCode?.toLowerCase().includes(filters?.search?.toLowerCase()) &&
          !item?.itemGroup?.toLowerCase().includes(filters?.search?.toLowerCase()) &&
          !item?.document?.toLowerCase().includes(filters?.search?.toLowerCase())
      ){
          return false
        }
    }

    const isWithinBounds = (value, lowerBound, upperBound) =>
      (!lowerBound || value >= lowerBound) &&
      (!upperBound || value <= upperBound);


    const matchesFilter = (filterArray, itemValue, key = 'value') =>
      !filterArray ||
      filterArray.length == 0 ||
      filterArray.some(filter => filter[key] == itemValue);

    const matchesFilterList = (filterArray, itemValue) =>
      !filterArray ||
      filterArray.length == 0 ||
      filterArray.some(filter => filter?.value?.id == itemValue);
      
    if (
      !matchesFilterList(filters.itemGroup?.map(itemGroup => ({value:{id:itemGroup.label}})), item.itemGroup)
    ) {
      return false;
    }

    if (
      !isWithinBounds(
        item.quantity,
        filters.quantityLowerBound,
        filters.quantityUpperBound
      ) ||
      !isWithinBounds(
        item.price,
        filters.priceLowerBound,
        filters.priceUpperBound
      ) ||
      !isWithinBounds(
        item.income,
        filters.incomeLowerBound,
        filters.incomeUpperBound
      ) ||
      (item?.docDate && !isWithinBounds(
        item.docDate ? new Date(item.docDate) : undefined,
        filters?.dateFrom ? new Date(filters.dateFrom) : undefined,
        filters?.dateTo ? new Date(filters.dateTo) : undefined
      ))
    ) {
      return false;
    }

    return true;
  };

  const createTableData = (prevTableData, data) => {
    const generateColumns = () => [
      {
        key: 'index',
        name: "#",
        sortable: true,
        groupable: false,
        type: 'number',
        active:
          prevTableData?.columns?.find(col => col.key === 'index')?.active ??
          true
      },
      {
        key: 'description',
        name: "Item",
        sortable: true,
        groupable: true,
        type: 'text',
        active:
          prevTableData?.columns?.find(col => col.key === 'description')
            ?.active ?? true
      },
      {
        key: 'itemGroup',
        name: "Item Group",
        sortable: true,
        groupable: true,
        type: 'text',
        active:
          prevTableData?.columns?.find(col => col.key === 'itemGroup')
            ?.active ?? true
      },
      {
        key: 'document',
        name: "S.O.",
        sortable: true,
        groupable: true,
        type: 'text',
        active:
          prevTableData?.columns?.find(col => col.key === 'document')
            ?.active ?? true
      },
      {
        key: 'doc_date',
        name: "Date",
        sortable: true,
        groupable: true,
        type: 'date',
        active:
          prevTableData?.columns?.find(col => col.key === 'doc_date')
            ?.active ?? true
      },
      createGroupColumn(
        'quantity',
        "Qty",
        undefined,
        false,
        'number'
      ),
      createGroupColumn(
        'price',
        "Price",
        DefaultCurrency,
        false,
        'currency',
        true,
        false
      ),
      createGroupColumn(
        'income',
        "Amount",
        DefaultCurrency,
        false,
        'currency'
      )
    ];

    const createGroupColumn = (
      key,
      name,
      Formatter,
      groupable = false,
      type,
      active = true,
      total = true
    ) => ({
      key,
      name,
      sortable: true,
      groupable,
      type,
      total,
      active:
        prevTableData?.columns?.find(col => col.key === key)?.active ??
        active,
      renderGroupCell: ({ childRows }) =>
        Formatter ? (
          <Formatter
            value={childRows.reduce((sum, row) => sum + row[`${key}Value`], 0)}
          />
        ) : (
          childRows.reduce((sum, row) => sum + row[key], 0)
        )
    });

    const formatRow = (item, index) => ({
      index: index + 1,
      description: item.description + ` (${item.itemCode})`,
      itemCode: item.itemCode,
      document: item.document,
      quantity: item.quantity,
      itemGroup: item.itemGroup,
      incomeValue: item.income,
      income: <DefaultCurrency value={item.income} />,
      price: <DefaultCurrency value={item.price} />,
      priceValue: item.price,
      doc_date: dateFormater(item.docDate),
      doc_date_value: item.docDate
    });
    const columns = generateColumns();
    const rows = (data || []).filter(isItemValidated).map((item, index) => {
      return formatRow(item, index);
    });
    setTableData({ columns, rows });
    setLoading(false);
  };

  const generateResult = () => {
    const prevTabelData = tableData;
    setTableData(undefined);
    setLoading(true);
    apollo
      .query({
        query: productSalesMasterListQuery,
        variables: {
          customer: formState.info.customer?.value?.id,
          salesman: appState?.auth?.user?.id
        }
      })
      .then(data => {
        setData(data.data.productSalesOrdersMasterList);
        createTableData(prevTabelData, data.data.productSalesOrdersMasterList);
      })
      .catch(() => {
        toast.error('Catching Data Failed');
        setLoading(false);
      });
  };

  const applyFilter = () => {
    const prevTabelData = tableData;
    setTableData(undefined);
    setLoading(true);
    createTableData(prevTabelData, data);
  };

  useEffect(() => {
    generateResult()
    
  }, []);

  useEffect(()=>{
    applyFilter()
    setFilterList({
      rows:[
        [
          {
            type: 'date',
            title: "Date From",
            value: filters.dateFrom,
            onChange: e =>
              setFilters({
                ...filters,
                dateFrom: e.target.value
              })
          },
          {
            type: 'date',
            title: "Date To",
            value: filters.dateTo,
            onChange: e =>
              setFilters({
                ...filters,
                dateTo: e.target.value
              })
          }
        ],
        [
          {
            type: 'select',
            active: true,
            isMulti: true,
            title: "Item Group",
            value: filters.itemGroup,
            options: appState.resources.itemGroups.map(itemGroup => ({
              value: itemGroup.id,
              label: itemGroup.name
            })),
            onChange: e =>
              setFilters({
                ...filters,
                itemGroup: e
              })
          },
          {
            type: 'range',
            active: true,
            title: "Qty",
            lowerLimitPlaceHolder: "from",
            upperLimitPlaceHolder: "to",
            lowerFilter: filters.quantityLowerBound,
            upperFilter: filters.quantityUpperBound,
            setLowerFilter: e =>
              setFilters({
                ...filters,
                quantityLowerBound: e.target.value
              }),
            setUpperFilter: e =>
              setFilters({
                ...filters,
                quantityUpperBound: e.target.value
              })
          },
          {
            type: 'range',
            active: true,
            title: "Price",
            lowerLimitPlaceHolder: "from",
            upperLimitPlaceHolder: "to",
              lowerFilter: filters.priceLowerBound,
            upperFilter: filters.priceUpperBound,
            setLowerFilter: e =>
              setFilters({
                ...filters,
                priceLowerBound: e.target.value
              }),
            setUpperFilter: e =>
              setFilters({
                ...filters,
                priceUpperBound: e.target.value
              })
          },
          {
            type: 'range',
            active: true,
            title: "Amount",
            lowerLimitPlaceHolder: "from",
            upperLimitPlaceHolder: "to",
            lowerFilter: filters.incomeLowerBound,
            upperFilter: filters.incomeUpperBound,
            setLowerFilter: e =>
              setFilters({
                ...filters,
                incomeLowerBound: e.target.value
              }),
            setUpperFilter: e =>
              setFilters({
                ...filters,
                incomeUpperBound: e.target.value
              })
          }
        ]
      ],
    })
  }, [filters])


  return (
    <Modal
      show={formState.customerOrderHistory}
      dialogClassName='w-90vw no-max-width'
      onHide={() => setFormState({ key: 'customerOrderHistory', value: false })}
    >
      <Modal.Header closeButton>
        <Modal.Title>{formState.info.customer?.value?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body className='h-80vh overflow-auto p-0'>
        {loading && 
          <LoadingOverlay show={true}/>
        }
        {!loading && 
          <React.Fragment>
            <TopToolBar
              filters={filterList}
              gentrateResult={generateResult}
              loading={loading}
              generateResultText="Generate Result"
            />
            <hr className='mt-0 '/>
            <AdvancedTable
              tableData={tableData}
              setTableData={setTableData}
              groupBy={groupBy}
              setGroupBy={setGroupBy}
              expandedGroupIds={expandedGroupIds}
              setExpandedGroupIds={setExpandedGroupIds}
              sortColumns={sortColumns}
              setSortColumns={setSortColumns}
              filters={filters}
              setFilters={setFilters}
            />
          </React.Fragment>
        }
      </Modal.Body>
    </Modal>
  );
};

export default CustomerOrderHistory;
